import { useState } from 'react';
import { ListItemIcon, Theme, Typography, ListItemButton } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  CcfDoubleArrowIcon,
  CcfTypography,
  CcfBox,
  CcfPlusIcon,
  CcfAccordion,
  CcfAccordionSummary,
  CcfAccordionDetails,
  CcfWorkingIcon,
  CcfAvailableIcon,
  CcfUnavailableIcon,
  CcfOfflineIcon,
  CcfMessageGroupIcon,
} from '@nice-ccf/ui-controls';
import { CcfBadge } from '@nice-ccf/ui-controls';
import {
  getSelectedAgent,
  ccfConversationsActions,
} from '../+state/ccf-conversations.slice';
import { useSelector, useDispatch } from 'react-redux';
import ConversationsAccordionStyles from './conversations-accordion.styles';
import { useTheme } from '@mui/material';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: '45px',
      padding: '0px 16px',
      paddingLeft: '7px',
      margin: '0',
      '&$expanded': {
        minHeight: '45px',
        margin: '0',
      },
    },
    content: {
      '&$expanded': {
        margin: '10px 0px',
      },
    },

    navList: {
      width: '100%',
      padding: '0px',
    },
    navListItemButton: {
      padding: '5px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      overflow: 'hidden',
      borderRadius: '4px',
    },
    navListItemIcon: {
      justifyContent: 'left',
      minWidth: 'auto',
    },
    hoverDisabledButton: {
      padding: '0px',
      '&:hover': {
        background: 'inherit',
      },
    },
    listItemRoot: {
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingInline: 0,
    },
    listItemSelected: {
      background: '#526b7a',
    },
    captionText: {
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '11px',
      lineHeight: '15px',
      color: '#8C95A5',
    },
    listItemName: {
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '12px',
      lineHeight: '15px',
      color: '#333333',
      marginBottom: '4px',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
    },
    groupIconRoot: {
      fontSize: '2rem',
    },
    statusIcon: {
      fontSize: '18px',
    },
    unreadMessageTxt: {
      color: 'white',
      backgroundColor: '#007AB8',
      borderRadius: '50%',
      padding: '2px 6px',
      fontSize: '10px',
      marginBottom: '10px',
    },
  })
);

/**
 * Function to be used for conversation accordion
 * @param param0 - React.ReactNode
 * @example <ConversationsAccordion />
 * @returns
 */
export const ConversationsAccordion = ({
  children,
  caption,
  showAddButton,
  type,
}: {
  children?: any;
  caption?: string;
  showAddButton?: boolean;
  type?: string;
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState<boolean>(true);
  const selectedAgent = useSelector(getSelectedAgent);
  const [activeItem, setActiveItem] = useState<string>(children[0]?.name || '');
  const theme = useTheme();
  const simStyles = ConversationsAccordionStyles(theme);
  const { updateSelectedAgent, updateAgentGroups, updateAgents } =
    ccfConversationsActions;

  /**
   * Function to render status icon
   * @param e - any
   * @example renderStatusIcon(e)
   */
  const renderStatusIcon = (status: string) => {
    switch (status) {
      case 'working':
        return <CcfWorkingIcon classes={{ root: styles.statusIcon }} />;
      case 'available':
        return <CcfAvailableIcon classes={{ root: styles.statusIcon }} />;
      case 'busy':
        return <CcfUnavailableIcon classes={{ root: styles.statusIcon }} />;
      case 'offline':
        return <CcfOfflineIcon classes={{ root: styles.statusIcon }} />;
      default:
        return <CcfAvailableIcon classes={{ root: styles.statusIcon }} />;
    }
  };

  /**
   * Function to update selected Agent
   * @param user - Agent
   * @example
   */
  const agentSelectHandler = (agent: any, type: any) => {
    setActiveItem(agent.name);
    if (!agent.isMessageRead) {
      agent = { ...agent, isMessageRead: true };
    }
    dispatch(updateSelectedAgent({ agent, type }));
    const action = type === 'group' ? updateAgentGroups : updateAgents;
    dispatch(action({ agent, type: 'update' }));
  };

  return (
    <CcfAccordion
      square
      sx={simStyles.accordionContainer}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
    >
      <CcfAccordionSummary
        aria-controls="detail-content"
        id={`agentMessages-${caption}`}
        className={styles.content}
        sx={simStyles.accordionHeader}
      >
        <CcfBox sx={simStyles.accordionTitleBlock}>
          <CcfBox sx={simStyles.accordionTitleSubBlock}>
            <CcfDoubleArrowIcon sx={simStyles.expandedIcon} />
            <CcfTypography sx={simStyles.accordionTitle}>
              {caption}{' '}
              <span style={{ marginLeft: '5px' }}>({children.length})</span>
            </CcfTypography>
          </CcfBox>
          {showAddButton && <CcfPlusIcon />}
        </CcfBox>
      </CcfAccordionSummary>
      <CcfAccordionDetails classes={{ root: styles.root }}>
        {children.map((item: any, index: number) => (
          <ListItemButton
            key={`user-${item.id}-${item.name}`}
            onClick={() => agentSelectHandler(item, type)}
            selected={item.name === selectedAgent?.name}
            className={styles.navListItemButton}
            style={{
              backgroundColor:
                item.name === selectedAgent?.name ? '#007AB8' : 'inherit',
            }}
          >
            <CcfBox sx={simStyles.accordionTitleSubBlock}>
              <ListItemIcon classes={{ root: styles.navListItemIcon }}>
                {showAddButton ? (
                  <CcfMessageGroupIcon
                    fillColor={item.name === selectedAgent?.name ? 'white' : ''}
                    classes={{ root: styles.statusIcon }}
                    style={{ marginLeft: '1px' }}
                  />
                ) : (
                  renderStatusIcon(item.status)
                )}
              </ListItemIcon>
              <CcfTypography
                className={styles.listItemName}
                style={{
                  fontWeight: item.isMessageRead ? 'normal' : 'bold',
                  color:
                    item.name === selectedAgent?.name ? 'white' : '#333333',
                }}
              >
                {item.name}
              </CcfTypography>
            </CcfBox>

            {!item.isMessageRead && (
              <CcfBox marginRight={'8px'}>
                <CcfBadge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  badgeContent={
                    <Typography className={styles.unreadMessageTxt}>
                      {item.messages.length}
                    </Typography>
                  }
                />
              </CcfBox>
            )}
          </ListItemButton>
        ))}
      </CcfAccordionDetails>
    </CcfAccordion>
  );
};
